import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  videos: Array<{
    id: string;
    title: string;
    thumbnail: string;
    views: string;
    duration: string;
  }>;
}

const categories: Category[] = [
  {
    id: 'funny',
    name: 'Funny Cats',
    icon: 'ri-emotion-laugh-fill',
    color: 'from-orange-500 to-rose-500',
    videos: [
      {
        id: 'OdFkjD_CtGU',
        title: 'Cats Being Hilarious Compilation',
        thumbnail: 'https://img.youtube.com/vi/OdFkjD_CtGU/maxresdefault.jpg',
        views: '2.4M',
        duration: '10:23'
      },
      {
        id: 'hY7m5jjJ9mM',
        title: 'Epic Cat Fails That Will Make You Laugh',
        thumbnail: 'https://img.youtube.com/vi/hY7m5jjJ9mM/maxresdefault.jpg',
        views: '1.8M',
        duration: '8:45'
      },
      {
        id: 'J---aiyznGQ',
        title: 'Funny Cats and Kittens Meowing',
        thumbnail: 'https://img.youtube.com/vi/J---aiyznGQ/maxresdefault.jpg',
        views: '3.2M',
        duration: '12:15'
      }
    ]
  },
  {
    id: 'cute',
    name: 'Cute Kittens',
    icon: 'ri-heart-fill',
    color: 'from-rose-500 to-pink-500',
    videos: [
      {
        id: 'tLt5rBfNucc',
        title: 'Adorable Kittens Playing Together',
        thumbnail: 'https://img.youtube.com/vi/tLt5rBfNucc/maxresdefault.jpg',
        views: '4.1M',
        duration: '15:30'
      },
      {
        id: 'CQ9Bc5L6T4I',
        title: 'Tiny Kittens Being Cute',
        thumbnail: 'https://img.youtube.com/vi/CQ9Bc5L6T4I/maxresdefault.jpg',
        views: '2.9M',
        duration: '9:20'
      },
      {
        id: 'J---aiyznGQ',
        title: 'Sleepy Kittens Compilation',
        thumbnail: 'https://img.youtube.com/vi/J---aiyznGQ/maxresdefault.jpg',
        views: '1.7M',
        duration: '11:05'
      }
    ]
  },
  {
    id: 'educational',
    name: 'Cat Care',
    icon: 'ri-book-open-fill',
    color: 'from-amber-500 to-orange-500',
    videos: [
      {
        id: 'GbIRUw_J4-E',
        title: 'Complete Guide to Cat Behavior',
        thumbnail: 'https://img.youtube.com/vi/GbIRUw_J4-E/maxresdefault.jpg',
        views: '890K',
        duration: '18:42'
      },
      {
        id: 'OdFkjD_CtGU',
        title: 'How to Train Your Cat',
        thumbnail: 'https://img.youtube.com/vi/OdFkjD_CtGU/maxresdefault.jpg',
        views: '1.2M',
        duration: '14:30'
      },
      {
        id: 'hY7m5jjJ9mM',
        title: 'Understanding Cat Body Language',
        thumbnail: 'https://img.youtube.com/vi/hY7m5jjJ9mM/maxresdefault.jpg',
        views: '750K',
        duration: '10:15'
      }
    ]
  },
  {
    id: 'rescue',
    name: 'Rescue Stories',
    icon: 'ri-hand-heart-fill',
    color: 'from-pink-500 to-rose-500',
    videos: [
      {
        id: 'CQ9Bc5L6T4I',
        title: 'Amazing Cat Rescue Transformation',
        thumbnail: 'https://img.youtube.com/vi/CQ9Bc5L6T4I/maxresdefault.jpg',
        views: '3.5M',
        duration: '16:20'
      },
      {
        id: 'tLt5rBfNucc',
        title: 'Saving Abandoned Kittens',
        thumbnail: 'https://img.youtube.com/vi/tLt5rBfNucc/maxresdefault.jpg',
        views: '2.1M',
        duration: '13:45'
      },
      {
        id: 'GbIRUw_J4-E',
        title: 'Heartwarming Cat Adoption Stories',
        thumbnail: 'https://img.youtube.com/vi/GbIRUw_J4-E/maxresdefault.jpg',
        views: '1.9M',
        duration: '20:10'
      }
    ]
  }
];

export default function FastVideoBrowser() {
  const [activeCategory, setActiveCategory] = useState<string>('funny');

  const currentCategory = categories.find(cat => cat.id === activeCategory) || categories[0];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
            <i className="ri-video-fill text-orange-500"></i>
            <span className="text-sm font-bold text-orange-700">Browse by Category</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            Find Your
            <span className="bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent"> Perfect Video</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore curated collections of the best cat content on the internet
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`group relative px-6 py-3 rounded-full font-bold transition-all duration-300 whitespace-nowrap ${
                activeCategory === category.id
                  ? 'text-white shadow-xl scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
              }`}
            >
              {activeCategory === category.id && (
                <div className={`absolute inset-0 bg-gradient-to-r ${category.color} rounded-full`}></div>
              )}
              <span className="relative flex items-center gap-2">
                <i className={`${category.icon} text-xl`}></i>
                {category.name}
              </span>
            </button>
          ))}
        </div>

        {/* Featured Video Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {currentCategory.videos.map((video, index) => (
            <Link
              key={video.id}
              to="/videos"
              className="group relative aspect-video rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Thumbnail */}
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />

              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300`}></div>

              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${currentCategory.color} flex items-center justify-center shadow-2xl transform group-hover:scale-125 transition-all duration-300`}>
                  <i className="ri-play-fill text-3xl text-white ml-1"></i>
                </div>
              </div>

              {/* Duration Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 bg-black/80 backdrop-blur-sm rounded-full text-white text-sm font-bold">
                {video.duration}
              </div>

              {/* Video Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white text-lg font-bold mb-2 line-clamp-2 drop-shadow-lg">
                  {video.title}
                </h3>
                <div className="flex items-center gap-4 text-white/90 text-sm">
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-fill"></i>
                    <span>{video.views} views</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className={`${currentCategory.icon}`}></i>
                    <span>{currentCategory.name}</span>
                  </div>
                </div>
              </div>

              {/* Hover Border Glow */}
              <div className={`absolute inset-0 rounded-3xl border-4 border-transparent group-hover:border-white/30 transition-all duration-300`}></div>
            </Link>
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <Link
            to="/videos"
            className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${currentCategory.color} text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap`}
          >
            <span>View All {currentCategory.name}</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
